/**
 * Agents screen (mockup `docs/mockups/phase-2/agents.html`): instances, templates, tools, models.
 * The library export/import strings stay in `core.ts` under `agents.library.*` — the library is the
 * fifth in-page section of this screen and its keys were shipped in phase 1.
 *
 * Keys are camelCase segments only: `scripts/guards/i18n-keys.mjs` cannot see a key built inside a
 * template literal, so enum values (`every_step`, `plan_and_irreversible`) are mapped to literal
 * keys in the page instead of interpolated.
 */
export const agents = {
  'agents.intro': 'Агенти — це члени команди: шаблон ролі, екземпляр для бізнесу, політика інструментів і місячний бюджет.',

  'agents.section.instances': 'Екземпляри',
  'agents.section.templates': 'Шаблони',
  'agents.section.tools': 'Інструменти',
  'agents.section.models': 'Моделі',

  'agents.filter.allBusinesses': 'Усі бізнеси',

  // ---- instances ----
  'agents.instances.title': 'Екземпляри',
  'agents.instances.new': 'Новий екземпляр',
  'agents.instances.empty': 'Ще немає жодного екземпляра.',
  'agents.instances.noBusinesses': 'Спершу створи бізнес — екземпляр агента належить бізнесу.',
  'agents.instances.noTemplates': 'Немає шаблонів ролей: спершу засій бібліотеку.',
  'agents.col.name': 'Назва',
  'agents.col.business': 'Бізнес',
  'agents.col.template': 'Шаблон',
  'agents.col.model': 'Модель',
  'agents.col.supervision': 'Нагляд',
  'agents.col.spent': 'Витрачено / бюджет',
  'agents.col.status': 'Статус',
  'agents.action.policy': 'Політика',
  'agents.action.edit': 'Редагувати',
  'agents.action.view': 'Переглянути',
  'agents.action.activate': 'Активувати',
  'agents.spent': '{spent} / {limit} {currency}',
  'agents.noBudget': 'Бюджет не задано',

  'agents.sup.everyStep': 'кожен результат',
  'agents.sup.planAndIrreversible': 'план і незворотні дії',
  'agents.sup.planOnly': 'лише план',

  'agents.status.draft': 'чернетка',
  'agents.status.active': 'активний',
  'agents.status.deprecated': 'застарілий',
  'agents.status.archived': 'в архіві',

  // ---- instance form ----
  'agents.form.new': 'Новий екземпляр',
  'agents.form.edit': 'Екземпляр: {name}',
  'agents.form.template': 'Шаблон ролі',
  'agents.form.business': 'Бізнес',
  'agents.form.name': 'Назва',
  'agents.form.model': 'Модель',
  'agents.form.credential': 'Ключ',
  'agents.form.supervision': 'Нагляд',
  'agents.form.status': 'Статус',
  'agents.form.instructionAdditions': 'Додаткові правила',
  'agents.form.tone': 'Тон',
  'agents.form.language': 'Мова',
  'agents.form.softLimit': 'М’який ліміт бюджету, USD',
  'agents.form.hardLimit': 'Жорсткий ліміт бюджету, USD',
  'agents.form.budget': 'Бюджет на цей місяць',
  'agents.form.none': 'Не задано',
  'agents.form.create': 'Створити',
  'agents.form.saved': 'Збережено',
  'agents.form.hint': 'Екземпляр не копіює шаблон: зберігається лише різниця переозначень і версія шаблону, до якої він прикріплений.',
  'agents.form.overrides': 'Переозначення',

  // ---- templates ----
  'agents.templates.title': 'Шаблони',
  'agents.templates.empty': 'Немає шаблонів ролей.',
  'agents.templates.col.slug': 'Slug',
  'agents.templates.col.version': 'Версія',
  'agents.templates.col.origin': 'Постачання',
  'agents.templates.col.instances': 'Екземплярів',
  'agents.templates.shipped': 'бібліотека',
  'agents.templates.own': 'власний',
  'agents.templates.version': 'v{n}',
  'agents.templates.drawer': 'Шаблон: {name}',
  'agents.templates.purpose': 'Призначення',
  'agents.templates.instruction': 'Інструкція',
  'agents.templates.defaultTools': 'Інструменти за замовчуванням',
  'agents.templates.activateHint': 'Шаблон стає активним лише після того, як власник прочитав і затвердив цей текст.',
  'agents.templates.activated': 'Шаблон активовано.',

  // ---- tools ----
  'agents.tools.title': 'Інструменти',
  'agents.tools.empty': 'Реєстр інструментів порожній: його наповнюють MCP-сервери під час запуску.',
  'agents.tools.col.server': 'Сервер',
  'agents.tools.col.tool': 'Інструмент',
  'agents.tools.col.effect': 'Побічний ефект',
  'agents.tools.col.idempotent': 'Ідемпотентний',
  'agents.tools.col.cost': 'Клас вартості',
  'agents.tools.col.enabled': 'Увімкнено',
  'agents.effect.none': 'немає',
  'agents.effect.reversible': 'зворотна',
  'agents.effect.irreversible': 'незворотна',
  'agents.tools.officePolicy': 'Політика офісу',
  'agents.tools.officeHint': 'Офісна політика — стеля: екземпляр може лише звузити цей перелік.',

  // ---- models ----
  'agents.models.title': 'Моделі',
  'agents.models.empty': 'Немає цін на моделі.',
  'agents.models.new': 'Додати модель',
  'agents.models.col.provider': 'Провайдер',
  'agents.models.col.model': 'Модель',
  'agents.models.col.input': 'Вхід $/M',
  'agents.models.col.output': 'Вихід $/M',
  'agents.models.col.cached': 'Кеш $/M',
  'agents.models.col.enabled': 'Увімкнено',
  'agents.models.drawerNew': 'Нова модель',
  'agents.models.drawerEdit': 'Модель: {name}',
  'agents.models.currency': 'Валюта',
  'agents.models.hint': 'Ціни — це рядки таблиці, а не константи: вартість прогону рахується з них.',

  // ---- policy drawer ----
  'agents.policy.instance': 'Політика екземпляра · {name}',
  'agents.policy.office': 'Політика офісу',
  'agents.policy.allowedTools': 'Дозволені інструменти',
  'agents.policy.noTools': 'Немає увімкнених інструментів.',
  'agents.policy.notInOffice': 'поза політикою офісу',
  'agents.policy.maxCostPerRun': 'Ліміт на прогін, USD',
  'agents.policy.maxCostPerDay': 'Ліміт на день, USD',
  'agents.policy.maxToolCalls': 'Макс. викликів інструментів',
  'agents.policy.maxExecutionTimeSec': 'Макс. час, с',
  'agents.policy.maxRetries': 'Макс. повторів',
  'agents.policy.approvalThreshold': 'Поріг затвердження, USD',
  'agents.policy.version': 'версія {n}',
  'agents.policy.saved': 'Політику збережено.',
  'agents.policy.empty': 'Порожній перелік означає, що агент не може викликати жодного інструмента.',

  // ---- errors ----
  'agents.error.versionConflict': 'Хтось змінив цей екземпляр раніше. Перезавантаж і повтори.',
  'agents.error.templateNotActive': 'Шаблон ще не активний: спочатку активуй його в розділі «Шаблони».',
  'agents.error.modelNotInCatalog': 'Такої увімкненої моделі немає в каталозі цін.',
  'agents.error.toolNotInOfficePolicy': 'Інструмента немає в політиці офісу: екземпляр може лише звузити її.',
  'agents.error.toolNotEnabled': 'Інструмент вимкнено.',
  'agents.error.modelExists': 'Ціна для цього провайдера і моделі вже є.',
  'agents.error.templateAlreadyActive': 'Шаблон уже активний.',
  'agents.error.agentRoleMissing': 'Немає системної ролі agent-default: засій базу.',
  'agents.error.credentialNotFound': 'Ключ не знайдено або він відкликаний.',
  'agents.reload': 'Перезавантажити',
} as const;
